/**
 * Content Collect provenance in the Library (CC-31 / SKE-4288).
 *
 * Ingest writes two things onto every collected asset: the `source` block the
 * Library already understands (`name` / `value` / `notes`), and a
 * `meta.contentCollect` record holding who submitted it, from which collect,
 * and what they consented to. This module is the only reader of that record —
 * the card badge and the detail panel both go through it, so "is this a
 * Content Collect asset?" is answered one way in one place.
 *
 * Everything here is defensive. A collect with identity off records no
 * submitter, a file-drop records no prompt, and an asset ingested before a
 * field existed simply lacks it; nothing may throw on a half-populated record,
 * because the asset itself is still perfectly usable.
 *
 * CONSENT IS A RECORD, NOT A CONTROL (D4). Expiry is surfaced so a brand can
 * see it, never to gate an action — no copy derived from here may say or imply
 * that an asset can't be used.
 */

import { SOURCE_NAMES } from "../../../constants/MediaLibrary";
import type { ContentCollectAssetMeta } from "../../../shared/types/files";

export const CONTENT_COLLECT_SOURCE_NAME = SOURCE_NAMES.CONTENT_COLLECT;

/**
 * The two fields the readers below actually touch. A real `CollectionFile`
 * satisfies this, and so does the handful of fixture objects the Collect
 * upsell's coded product mock feeds the badge (SKE-4862), which is how that
 * mock shows the REAL badge instead of a lookalike that can drift from it.
 */
export type ContentCollectProvenanceSource = {
  meta?: { contentCollect?: ContentCollectAssetMeta } | null;
  source?: { name?: string } | null;
};

/** `meta.contentCollect`, or null when the asset didn't come from a collect. */
export function getContentCollectMeta(
  asset: ContentCollectProvenanceSource | null | undefined
): ContentCollectAssetMeta | null {
  const meta = asset?.meta?.contentCollect;

  return meta && typeof meta === "object" ? meta : null;
}

/**
 * The badge/panel test. `source.name` is checked as well as the meta record so
 * an asset whose ingest predates `meta.contentCollect` — or whose meta was
 * trimmed somewhere downstream — is still labelled by the same string the
 * Source filter matches on.
 */
export function isContentCollectAsset(
  asset: ContentCollectProvenanceSource | null | undefined
): boolean {
  if (getContentCollectMeta(asset)) return true;

  return asset?.source?.name === CONTENT_COLLECT_SOURCE_NAME;
}

/**
 * How the contributor is named in the panel, or null when the collect never
 * asked. Null is rendered as "Submitted anonymously" rather than blank: an
 * empty row reads like missing data, when in fact it is the privacy setting
 * working as configured.
 */
export function contentCollectSubmitter(
  meta: ContentCollectAssetMeta | null | undefined
): { name: string | null; email: string | null } | null {
  const submitter = meta?.submitter;
  if (!submitter) return null;

  const name = submitter.name?.trim() || null;
  const email = submitter.email?.trim() || null;
  if (!name && !email) return null;

  return { name, email };
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Date + time, for "when did this arrive". */
export function formatCollectDateTime(
  value: string | null | undefined
): string | null {
  const parsed = parseDate(value);
  if (!parsed) return null;

  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Date only, for rights expiry — the time of day is noise there. */
export function formatCollectDate(
  value: string | null | undefined
): string | null {
  const parsed = parseDate(value);
  if (!parsed) return null;

  return parsed.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Whether the recorded usage rights have passed their end date. `now` is a
 * parameter so the test doesn't have to travel in time.
 */
export function rightsHaveExpired(
  meta: ContentCollectAssetMeta | null | undefined,
  now: Date = new Date()
): boolean {
  const expires = parseDate(meta?.consent?.rightsExpiresAt);

  return expires ? expires.getTime() < now.getTime() : false;
}

/**
 * The consent snapshot stores permitted-use KEYS (slugs) — the human labels
 * live on the collect, which the Library doesn't load. Un-slugging the key is
 * a faithful rendering of what we have: "social-posts" was slugged from the
 * label "Social posts" in the builder.
 */
export function humanizePermittedUse(key: string): string {
  const words = key.replaceAll(/[_-]+/g, " ").trim();
  if (!words) return key;

  return words.charAt(0).toUpperCase() + words.slice(1);
}
